import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import Loader from "src/Components/Common/Loader";
import MonetaryInput from "src/Components/Common/MonetaryInput";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { ViewEmailThreadRes } from "src/Components/Recon360/DisputeResolution/Common/ViewReplyMailThread";
import { DateString, UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import FormatDate from "src/Utils/DateUtils";
import {
  BcCustomisationDetails,
  BCPortalCBDetails,
  BcPortalDetails,
  CommunicationDetails,
} from "../../../BalanceConfirmationPortal/BalanceConfirmationPortalCommon";
import { BcPortalContext } from "../../../BalanceConfirmationPortal/BalanceConfirmationPortalContext";
import { DEFAULT_BRANCH_CODE, DEFAULT_COMPANY_ID } from "../../../CommonLegacy/CommonComponents";
import { NdAsyncButton } from "../../../MsmePartnerPortal/CommonComponents";
import $ from "../../../MsmePartnerPortal/PartnerPortal.module.scss";
import BcNewContext from "../BcNewContext";
import { UploadAndViewFileSection } from "./Common";

interface UpdateResponseDetailsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const UpdateResponseLRStep = ({ open, setOpen }: UpdateResponseDetailsProps) => {
  const { confirmStatus, setPageStatus, customization, setCustomization } = useContext(BcPortalContext);
  const { companyId, branchCode, currentRow } = useContext(BcNewContext);
  const { actor } = useContext(userContext);

  const bpName = currentRow?.businessPartnerName;
  const ruName = actor?.actorName;

  const [loading, setLoading] = useState(false);
  // const [mailThreads, setMailThreads] = useState<MsmeMailThread[]>([]);
  const [, setEmailsLists] = useState<{
    ownSideEmails: string[];
    toEmails: string[];
    ccEmails: string[];
  }>({
    ownSideEmails: [],
    toEmails: [],
    ccEmails: [],
  });
  const [allAttachments, setAllAttachments] = useState<{ name: string; link: string }[]>([]);

  const [bcPortalDetails, setBcPortalDetails] = useState<BcPortalDetails>({} as any);
  const customizationDetails: BcCustomisationDetails = customization.ledgerRequest;
  const [closingBalanceDate, setClosingBalanceDate] = useState<DateString>(null);
  const [currency, setCurrency] = useState<string>("INR");
  const [closingBalance, setClosingBalance] = useState<number>(null);
  const closingBalanceRef = useRef<number>(null);
  const [accountingSign, setAccountingSign] = useState<"R" | "P">(null);
  const [cbError, setCbError] = useState<boolean>(false);

  const [ledgersCount, setLedgersCount] = useState<number>(0);
  const [openItemsCount, setOpenItemsCount] = useState<number>(0);

  const onClose = () => {
    setOpen(false);
  };

  const GetClosingBalanceDetailsForTool = () => {
    useFetch<{ closingBalanceDetails: BCPortalCBDetails }>(
      "/api/PartnerCommunicationPortal/GetClosingBalanceDetailsForTool",
      "GET",
      {
        failureMessage: "Unable to fetch Closing Balance Details",
        config: {
          params: {
            mappingId: currentRow.mappingId,
            communicationId: currentRow.newMailInitiationCount,
            companyId: currentRow.companyId,
            branchCode: currentRow.branchCode,
          },
        },
        thenCallBack: (res) => {
          setClosingBalanceDate(res.data.closingBalanceDetails?.closingBalanceDate);
          setCurrency(res.data.closingBalanceDetails.currency ? res.data.closingBalanceDetails.currency : "INR");
          setAccountingSign(res.data.closingBalanceDetails.businessPartnerClosingBalance > 0 ? "R" : "P");
          const closingBalance =
            res.data.closingBalanceDetails.businessPartnerClosingBalance < 0
              ? -1 * res.data.closingBalanceDetails.businessPartnerClosingBalance
              : res.data.closingBalanceDetails.businessPartnerClosingBalance;
          setClosingBalance(closingBalance);
          closingBalanceRef.current = closingBalance;
        },
      }
    );
  };

  const GetDetailsForUploadingPageForTool = () => {
    return useFetch<{ details: BcPortalDetails }>(
      "/api/PartnerCommunicationPortal/GetDetailsForUploadingPageForTool",
      "GET",
      {
        failureMessage: "Failed to fetch details for uploading page",
        config: {
          params: {
            companyId: companyId || DEFAULT_COMPANY_ID,
            branchCode: branchCode || DEFAULT_BRANCH_CODE,
            mappingId: currentRow?.mappingId,
            communicationId: currentRow?.newMailInitiationCount,
            businessPartnerId: currentRow?.businessPartnerId,
            useCaseType: UseCaseType.balanceConfirmationBeta,
          },
        },
        thenCallBack: (res) => {
          if (res.data.details) {
            setBcPortalDetails(res.data.details);
          }
        },
      }
    );
  };

  const GetCommunicationTypeDetailsForTool = () => {
    return useFetch<CommunicationDetails>("/api/PartnerCommunicationPortal/GetCommunicationTypeDetailsForTool", "GET", {
      failureMessage: "Failed to fetch details for uploading page",
      config: {
        params: {
          companyId: companyId || DEFAULT_COMPANY_ID,
          branchCode: branchCode || DEFAULT_BRANCH_CODE,
          mappingId: currentRow?.mappingId,
          communicationId: currentRow?.newMailInitiationCount,
          businessPartnerId: currentRow?.businessPartnerId,
          useCaseType: UseCaseType.balanceConfirmationBeta,
        },
      },
      thenCallBack: (res) => {
        const details = res.data;
        const defaultCustomisation: BcCustomisationDetails = {
          isPdfRequired: null,
          isPdfOptional: null,
          isLedgerRequired: null,
          isOpenItemRequired: null,
          isPdfHidden: null,
          isLedgerHidden: null,
          isOpenItemHidden: null,
          isPartnerBalanceRequired: null,
          isPartnerBalanceHidden: null,
        };

        let customisationIfBalanceConfirmed: BcCustomisationDetails;
        let customisationIfBalanceNotConfirmed: BcCustomisationDetails;
        let customisationIfLedgerRequest: BcCustomisationDetails;

        if (details?.customisationIfConfirmed?.ledger) {
          customisationIfBalanceConfirmed = {
            isPdfRequired: details?.customisationIfConfirmed?.pdf === "yes",
            isPdfOptional: details?.customisationIfConfirmed?.pdf === "optional",
            isLedgerRequired: details?.customisationIfConfirmed?.ledger === "yes",
            isOpenItemRequired: details?.customisationIfConfirmed?.openItem === "yes",
            isPdfHidden: details?.customisationIfConfirmed?.pdf === "no",
            isLedgerHidden: details?.customisationIfConfirmed?.ledger === "no",
            isOpenItemHidden: details?.customisationIfConfirmed?.openItem === "no",
            isPartnerBalanceRequired: details?.customisationIfLedgerRequest?.partnerBalance === "yes",
            isPartnerBalanceHidden: details?.customisationIfLedgerRequest?.partnerBalance === "no",
          };
        }

        if (details?.customisationIfNotConfirmed?.ledger) {
          customisationIfBalanceNotConfirmed = {
            isPdfRequired: details?.customisationIfNotConfirmed?.pdf === "yes",
            isPdfOptional: details?.customisationIfNotConfirmed?.pdf === "optional",
            isLedgerRequired: details?.customisationIfNotConfirmed?.ledger === "yes",
            isOpenItemRequired: details?.customisationIfNotConfirmed?.openItem === "yes",
            isPdfHidden: details?.customisationIfNotConfirmed?.pdf === "no",
            isLedgerHidden: details?.customisationIfNotConfirmed?.ledger === "no",
            isOpenItemHidden: details?.customisationIfNotConfirmed?.openItem === "no",
            isPartnerBalanceRequired: details?.customisationIfLedgerRequest?.partnerBalance === "yes",
            isPartnerBalanceHidden: details?.customisationIfLedgerRequest?.partnerBalance === "no",
          };
        }

        if (details?.customisationIfLedgerRequest?.ledger) {
          customisationIfLedgerRequest = {
            isPdfRequired: false,
            isPdfOptional: false,
            isLedgerRequired: details?.customisationIfLedgerRequest?.ledger === "yes",
            isOpenItemRequired: details?.customisationIfLedgerRequest?.openItem === "yes",
            isPdfHidden: true,
            isLedgerHidden: details?.customisationIfLedgerRequest?.ledger === "no",
            isOpenItemHidden: details?.customisationIfLedgerRequest?.openItem === "no",
            isPartnerBalanceRequired: details?.customisationIfLedgerRequest?.partnerBalance === "yes",
            isPartnerBalanceHidden: details?.customisationIfLedgerRequest?.partnerBalance === "no",
          };
        }

        setCustomization({
          balanceConfirmed: customisationIfBalanceConfirmed || defaultCustomisation,
          balanceNotConfirmed: customisationIfBalanceNotConfirmed || defaultCustomisation,
          ledgerRequest: customisationIfLedgerRequest || defaultCustomisation,
        });
      },
    });
  };

  const ViewEmailThread = () => {
    return useFetch<ViewEmailThreadRes>(API_ENDPOINTS.VIEW_EMAIL_THREAD.url, "GET", {
      failureMessage: API_ENDPOINTS.VIEW_EMAIL_THREAD.failureMessage,
      config: {
        params: {
          companyId: companyId || DEFAULT_COMPANY_ID,
          branchCode: branchCode || DEFAULT_BRANCH_CODE,
          businessPartnerId: currentRow?.businessPartnerId,
          mappingId: currentRow?.mappingId,
          communicationTypeId: currentRow.communicationTypeId,
        },
      },
      thenCallBack: (res) => {
        if (res.data) {
          // setMailThreads(res.data?.emailThread?.length > 0 ? res.data?.emailThread : []);
          // setEmailSubject(res.data.emailSubject || "");
          setEmailsLists({
            ownSideEmails: res.data.ownSideEmails || [],
            toEmails: res.data.toEmails || [],
            ccEmails: res.data.ccEmails || [],
          });
          const allAttachments = res.data.emailThread.reduce((acc, email) => {
            if (email.attachments) {
              email.attachments.forEach((attachment, index) => {
                acc.push({ name: attachment, link: email.s3Link[index] });
              });
            }
            return acc;
          }, [] as { name: string; link: string }[]);

          setAllAttachments(allAttachments || []);
        }
      },
    });
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      GetDetailsForUploadingPageForTool(),
      GetCommunicationTypeDetailsForTool(),
      ViewEmailThread(),
      GetClosingBalanceDetailsForTool(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  const updatePartnerClosingBalance = (accountingSign: string) => {
    if (closingBalanceRef.current === null && customizationDetails?.isPartnerBalanceRequired) {
      setCbError(true);
      return;
    } else {
      setCbError(false);
    }

    useFetch("/api/PartnerCommunicationPortal/UpdatePartnerClosingBalanceFromTool", "POST", {
      failureMessage: "Unable to update Closing Balance",
      showSuccessToast: true,
      data: {
        closingBalance: accountingSign === "R" ? closingBalanceRef.current : -1 * closingBalanceRef.current,
        mappingId: currentRow.mappingId,
        companyId: currentRow.companyId,
        branchCode: currentRow.branchCode,
        communicationId: currentRow.newMailInitiationCount,
      },
    });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 900,
          },
        }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Update Response (Step: 2/3)
          </Typography>
        </DialogTitle>
        {loading ? (
          <Loader />
        ) : (
          <DialogContent dividers>
            <Stack className="fade_in details_box" maxWidth={900} mx={"auto"}>
              <FormControl sx={{ gap: 3 }}>
                {!customizationDetails?.isPartnerBalanceHidden && (
                  <Box className="step_1">
                    <Stack className="step_content">
                      <Typography py={1} sx={{ "& > b": { fontFamily: "inherit" } }}>
                        Closing Balance in <b>{bpName}</b> ledger, as on{" "}
                        <b>{FormatDate(closingBalanceDate).toString()}</b> for <b>{ruName}</b>
                      </Typography>
                      <Box className="d_flex" gap={2}>
                        <TextField
                          className={$.BR_fix}
                          sx={{
                            width: 186,
                            "& *.MuiInputBase-root": {
                              borderRadius: "4px !important",
                              borderColor: "transparent !important",
                            },
                            "& *": {
                              borderColor: "transparent !important",
                            },
                          }}
                          disabled={true}
                        />
                        <MonetaryInput
                          fullWidth
                          key={"closingBalance"}
                          className={$.BR_fix}
                          placeholder="Enter Closing Balance"
                          label="Enter Closing Balance"
                          returnNull={true}
                          value={closingBalance}
                          allowNegative={false}
                          setValue={(value: number) => {
                            setClosingBalance(value);
                            closingBalanceRef.current = value;
                          }}
                          onBlur={() => {
                            updatePartnerClosingBalance(accountingSign);
                          }}
                          disabled={confirmStatus}
                          focused={false}
                          autoFocus={false}
                          error={cbError}
                          helperText={cbError ? "Please enter Closing Balance" : ""}
                          currency={"INR"}
                          sx={{
                            "input::placeholder": {
                              fontSize: "14px",
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px !important",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <>
                                <TextField
                                  select
                                  className={$.BR_fix}
                                  value={accountingSign}
                                  onChange={(e) => {
                                    setAccountingSign(e.target.value as any);
                                    updatePartnerClosingBalance(e.target.value);
                                  }}
                                  sx={{
                                    minWidth: 150,
                                    position: "absolute",
                                    left: -165,
                                    "& *.MuiInputBase-root": {
                                      borderRadius: "4px !important",
                                    },
                                  }}
                                  disabled={confirmStatus}
                                >
                                  <MenuItem value={"R"}>Receivable</MenuItem>
                                  <MenuItem value={"P"}>Payable</MenuItem>
                                </TextField>
                                <Typography sx={{ marginRight: 2 }}>{currency}</Typography>
                              </>
                            ),
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                )}
                <Box className="step_2">
                  <Stack className="step_content" gap={2}>
                    {!customizationDetails?.isLedgerHidden && (
                      <UploadAndViewFileSection
                        fileType={"ledgerFile"}
                        initialFiles={bcPortalDetails?.ledgers?.map((ledger) => ({ name: ledger }))}
                        mailAttachmentFiles={allAttachments}
                        updateDocumentCount={setLedgersCount}
                      />
                    )}
                    {!customizationDetails?.isOpenItemHidden && (
                      <UploadAndViewFileSection
                        fileType={"openItem"}
                        initialFiles={bcPortalDetails?.openItems?.map((openItem) => ({ name: openItem }))}
                        mailAttachmentFiles={allAttachments}
                        updateDocumentCount={setOpenItemsCount}
                      />
                    )}
                  </Stack>
                </Box>
              </FormControl>
              {/* end of main steps flow */}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              setPageStatus("NotFilled");
              onClose();
            }}
          >
            Cancel
          </Button>
          <NdAsyncButton
            disabled={
              (customizationDetails?.isPartnerBalanceRequired && cbError) ||
              (customizationDetails?.isLedgerRequired && ledgersCount === 0) ||
              (customizationDetails?.isOpenItemRequired && openItemsCount === 0)
            }
            variant="contained"
            onClick={async () => {
              setPageStatus("FinalPage");
            }}
          >
            Submit
          </NdAsyncButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default UpdateResponseLRStep;
